/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table';
import { twMerge } from 'tailwind-merge';
import { Card, CardHeader, CardContent, CardFooter } from '../Card';
import { renderIcon } from '../Dashboard/iconUtils';
import { cn } from '../../../lib/utils';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
// Icons
const ChevronUpIcon = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 15l7-7 7 7"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 19l-7-7 7-7"
    />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5l7 7-7 7"
    />
  </svg>
);

// Pre-built render functions
const renderBadge = (
  t: TFunction<'pleri/dashboard'>,
  value: any,
  options?: { badgeColors?: { [key: string]: string } },
) => {
  const colorMap = options?.badgeColors || {
    Active: '#10b981',
    Inactive: '#6b7280',
    Pending: '#f59e0b',
    Completed: '#3b82f6',
    Failed: '#ef4444',
    Draft: '#8b5cf6',
    Published: '#059669',
    Archived: '#9ca3af',
  };

  const color = colorMap[value] || '#6b7280';

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '0.375rem',
        padding: '0.25rem 0.75rem',
        fontSize: '0.75rem',
        fontWeight: '500',
        backgroundColor: `${color}08`, // 8% opacity for subtle background
        color: color,
        border: `1px solid ${color}`, // 25% opacity for subtle border
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        letterSpacing: '0.025em',
        textTransform: 'capitalize',
        minWidth: 'fit-content',
        whiteSpace: 'nowrap',
      }}
    >
      {[true, false].includes(value) ? (value ? t('true') : t('false')) : value}
    </span>
  );
};

const renderSeverity = (
  value: any,
  options?: { severityColors?: { [key: string]: string } },
) => {
  const colorMap = options?.severityColors || {
    Critical: '#dc2626',
    High: '#ea580c',
    Medium: '#d97706',
    Low: '#65a30d',
    Info: '#2563eb',
  };

  const color = colorMap[value] || '#6b7280';

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '0.375rem',
        padding: '0.25rem 0.75rem',
        fontSize: '0.75rem',
        fontWeight: '500',
        backgroundColor: `${color}08`, // 8% opacity for subtle background
        color: color,
        border: `1px solid ${color}`, // 25% opacity for subtle border
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        letterSpacing: '0.025em',
        textTransform: 'capitalize',
        minWidth: 'fit-content',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </span>
  );
};

const renderStatus = (
  value: any,
  options?: {
    statusColors?: { [key: string]: string };
    statusIcons?: { [key: string]: string };
  },
) => {
  const colorMap = options?.statusColors || {
    Online: '#10b981',
    Offline: '#6b7280',
    Away: '#f59e0b',
    Busy: '#ef4444',
    Available: '#3b82f6',
    Unavailable: '#9ca3af',
  };

  const iconMap = options?.statusIcons || {
    Online: 'wifi',
    Offline: 'wifi-off',
    Away: 'clock',
    Busy: 'phone-off',
    Available: 'check-circle',
    Unavailable: 'x-circle',
  };

  const color = colorMap[value] || '#6b7280';
  const iconName = iconMap[value];

  // Get the icon component using the centralized utility
  const IconComponent = iconName
    ? renderIcon({ name: iconName, size: 12, color })
    : null;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        borderRadius: '0.375rem',
        padding: '0.25rem 0.75rem',
        fontSize: '0.75rem',
        fontWeight: '500',
        backgroundColor: `${color}08`, // 8% opacity for subtle background
        color: color,
        border: `1px solid ${color}25`, // 25% opacity for subtle border
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        letterSpacing: '0.025em',
        textTransform: 'capitalize',
        minWidth: 'fit-content',
        whiteSpace: 'nowrap',
      }}
    >
      {IconComponent || (
        <div
          style={{
            width: '0.375rem',
            height: '0.375rem',
            borderRadius: '50%',
            backgroundColor: color,
          }}
        />
      )}
      {value}
    </span>
  );
};

const renderDate = (value: any, options?: Column['renderOptions']) => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  const format = options?.dateFormat || 'MMM dd, yyyy';

  // Simple date formatting (you can enhance this with a date library)
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = months[date.getMonth()];
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();

  return (
    <span className="text-gray-900 dark:text-gray-100">
      {format === 'MMM dd, yyyy'
        ? `${month} ${day}, ${year}`
        : date.toLocaleDateString()}
    </span>
  );
};

const renderNumber = (value: any, options?: Column['renderOptions']) => {
  if (value === null || value === undefined) {
    return '-';
  }
  const num = Number(value);
  if (isNaN(num)) {
    return value;
  }

  const {
    decimals = 0,
    prefix = '',
    suffix = '',
  } = options?.numberFormat || {};
  const formatted = num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span className="font-mono text-gray-900 dark:text-gray-100">
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
};

const renderCurrency = (value: any, options?: Column['renderOptions']) => {
  if (value === null || value === undefined) {
    return '-';
  }
  const num = Number(value);
  if (isNaN(num)) {
    return value;
  }

  const { code = 'USD', decimals = 2 } = options?.currency || {};
  const formatted = num.toLocaleString(undefined, {
    style: 'currency',
    currency: code,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span className="font-mono text-gray-900 dark:text-gray-100">
      {formatted}
    </span>
  );
};

const renderEmail = (value: any) => {
  if (!value) {
    return '-';
  }
  return (
    <a
      href={`mailto:${value}`}
      style={{
        color: '#2563eb',
        textDecoration: 'none',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = '#1d4ed8';
        e.currentTarget.style.textDecoration = 'underline';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = '#2563eb';
        e.currentTarget.style.textDecoration = 'none';
      }}
    >
      {value}
    </a>
  );
};

const renderUrl = (value: any) => {
  if (!value) {
    return '-';
  }
  return (
    <a
      href={value}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: '#2563eb',
        textDecoration: 'none',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = '#1d4ed8';
        e.currentTarget.style.textDecoration = 'underline';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = '#2563eb';
        e.currentTarget.style.textDecoration = 'none';
      }}
    >
      {value}
    </a>
  );
};

const renderProgress = (
  value: any,
  options?: { progress?: { max?: number; color?: string } },
) => {
  if (value === null || value === undefined) {
    return '-';
  }
  const num = Number(value);
  if (isNaN(num)) {
    return value;
  }

  const max = options?.progress?.max || 100;
  const percentage = Math.min((num / max) * 100, 100);
  const color = options?.progress?.color || '#3b82f6';

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '0.75rem',
          color: '#4b5563',
          marginBottom: '0.25rem',
        }}
      >
        <span>{num}</span>
        <span>{percentage.toFixed(0)}%</span>
      </div>
      <div
        style={{
          width: '100%',
          backgroundColor: '#e5e7eb',
          borderRadius: '9999px',
          height: '0.5rem',
        }}
      >
        <div
          style={{
            height: '0.5rem',
            borderRadius: '9999px',
            backgroundColor: color,
            width: `${percentage}%`,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  );
};

const renderArray = (value: any, _options?: Column['renderOptions']) => {
  if (!value) {
    return '-';
  }
  if (!Array.isArray(value)) {
    return value;
  }
  if (value.length === 0) {
    return '-';
  }

  return (
    <span className="text-gray-900 dark:text-gray-100">{value.join(', ')}</span>
  );
};

// Helper function to render content with optional icon
const renderContentWithIcon = (content: React.ReactNode, column: Column) => {
  if (!column.icon) {
    return content;
  }

  const iconName = column.icon.name;
  const color = column.icon.color || '#6b7280';
  const size = column.icon.size || 16;
  const position = column.icon.position || 'left';
  const spacing = column.icon.spacing || 8;

  if (!iconName) {
    return content;
  }

  // Get the icon component using the centralized utility
  const iconElement = renderIcon({ name: iconName, size, color: '#ffffff' });

  if (!iconElement) {
    return content;
  }

  const iconContainer = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: color,
        borderRadius: '30%',
        width: `${size + 16}px`,
        height: `${size + 16}px`,
        padding: '8px',
        flexShrink: 0,
      }}
    >
      {iconElement}
    </div>
  );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: `${spacing}px`,
        flexDirection: position === 'right' ? 'row-reverse' : 'row',
        width: '100%',
        minWidth: 0,
      }}
    >
      {iconContainer}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {content}
      </div>
    </div>
  );
};

// Main render function that handles all render types
const renderCell = (
  t: TFunction<'pleri/dashboard'>,
  value: any,
  column: Column,
  row: any,
) => {
  let renderedContent: React.ReactNode;

  // If custom render function is provided, use it
  if (column.render) {
    renderedContent = column.render(value, row);
  }
  // If renderType is specified, use pre-built renderers
  else if (column.renderType) {
    switch (column.renderType) {
      case 'badge':
        renderedContent = renderBadge(t, value, column.renderOptions);
        break;
      case 'severity':
        renderedContent = renderSeverity(value, column.renderOptions);
        break;
      case 'status':
        renderedContent = renderStatus(value, column.renderOptions);
        break;
      case 'date':
        renderedContent = renderDate(value, column.renderOptions);
        break;
      case 'number':
        renderedContent = renderNumber(value, column.renderOptions);
        break;
      case 'currency':
        renderedContent = renderCurrency(value, column.renderOptions);
        break;
      case 'email':
        renderedContent = renderEmail(value);
        break;
      case 'url':
        renderedContent = renderUrl(value);
        break;
      case 'progress':
        renderedContent = renderProgress(value, column.renderOptions);
        break;
      case 'array':
        renderedContent = renderArray(value, column.renderOptions);
        break;
      default:
        renderedContent = value;
    }
  } else {
    // Default: return value as is
    renderedContent = value;
  }

  // Apply icon if configured
  return renderContentWithIcon(renderedContent, column);
};

export interface Column {
  key: string;
  header: string;
  sortable?: boolean;
  filterable?: boolean;
  render?: (value: any, row: any) => React.ReactNode;
  renderType?:
    | 'badge'
    | 'severity'
    | 'status'
    | 'date'
    | 'number'
    | 'currency'
    | 'email'
    | 'url'
    | 'progress'
    | 'array';
  // Icon configuration for any column type
  icon?: {
    name?: string; // icon name (can be from data or static)
    color?: string; // hex color for icon background
    size?: number; // icon size in pixels
    position?: 'left' | 'right'; // icon position relative to content
    spacing?: number; // spacing between icon and content
  };
  renderOptions?: {
    // Badge options - hex colors for backend compatibility
    badgeColors?: {
      [key: string]: string; // key -> hex color mapping
    };
    // Severity options - hex colors for backend compatibility
    severityColors?: {
      [key: string]: string; // severity -> hex color mapping
    };
    // Status options - hex colors for backend compatibility
    statusColors?: {
      [key: string]: string; // status -> hex color mapping
    };
    // Status icons - icon names for backend compatibility
    statusIcons?: {
      [key: string]: string; // status -> icon name mapping
    };
    // Date options
    dateFormat?: string;
    // Number options
    numberFormat?: {
      decimals?: number;
      prefix?: string;
      suffix?: string;
    };
    // Currency options
    currency?: {
      code?: string;
      decimals?: number;
    };
    // Progress options
    progress?: {
      max?: number;
      color?: string; // hex color
    };
  };
  style?: React.CSSProperties; // CSS styles as object for backend compatibility
  minWidth?: number;
  maxWidth?: number;
  align?: 'left' | 'center' | 'right';
}

export interface DataTableProps {
  /** The title of the table */
  title?: string;
  /** Array of column definitions */
  columns?: Column[];
  /** Array of data rows */
  data?: any[];
  /** Optional subtitle */
  subtitle?: string;
  /** Whether the table is loading */
  isLoading?: boolean;
  /** Optional custom className for styling */
  className?: string;
  /** Optional click handler for rows */
  onRowClick?: (row: any) => void;
  /** Whether to show pagination */
  showPagination?: boolean;
  /** Number of items per page (default: 10) */
  itemsPerPage?: number;
  /** Optional empty state message */
  emptyMessage?: string;
  /** Whether data is streaming from LLM */
  isStreaming?: boolean;
}

export const DataTable: React.FC<DataTableProps> = ({
  title,
  columns = [],
  data = [],
  subtitle,
  isLoading = false,
  className,
  onRowClick,
  showPagination = false,
  itemsPerPage = 10,
  emptyMessage = 'No data available',
}) => {
  const { t } = useTranslation('pleri/dashboard');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  // Convert columns to TanStack Table format
  const tableColumns = useMemo<ColumnDef<any>[]>(() => {
    return columns.map((column) => ({
      accessorKey: column.key,
      header: ({ column: col }) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {column.header}
          </span>
          {column.sortable && (
            <button
              onClick={col.getToggleSortingHandler()}
              className="rounded p-1 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {col.getIsSorted() === 'asc' ? (
                <ChevronUpIcon />
              ) : col.getIsSorted() === 'desc' ? (
                <ChevronDownIcon />
              ) : (
                <div className="h-4 w-4 opacity-0 group-hover:opacity-50" />
              )}
            </button>
          )}
        </div>
      ),
      cell: ({ getValue, row }) => {
        const value = getValue();
        return renderCell(t, value, column, row.original);
      },
      enableSorting: column.sortable ?? false,
      enableColumnFilter: column.filterable ?? false,
      minSize: column.minWidth,
      maxSize: column.maxWidth,
      meta: {
        style: column.style,
        align: column.align || 'left',
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(columns)]);

  const finalData = useMemo(() => {
    return data.map((item) => {
      return {
        ...item,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data)]);

  const table = useReactTable({
    data: finalData,
    columns: tableColumns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableSorting: true,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    globalFilterFn: 'includesString',
    initialState: {
      pagination: {
        pageSize: itemsPerPage,
      },
    },
  });

  // Show loading state if no data is provided and not explicitly loading
  if (
    isLoading ||
    (!title && !subtitle && columns.length === 0 && data.length === 0)
  ) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="w-full animate-pulse">
            <div className="mb-4 h-4 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
            <div className="space-y-3">
              <div className="h-8 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="h-6 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="h-6 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="h-6 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const renderLoadingContent = () => (
    <div className="animate-pulse">
      {title && (
        <div className="mb-4 h-6 w-48 rounded bg-gray-200 dark:bg-gray-700"></div>
      )}
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="h-4 rounded bg-gray-200 dark:bg-gray-700"
          ></div>
        ))}
      </div>
    </div>
  );

  const renderTable = () => (
    <div
      className={twMerge(
        'custom-scrollbar overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700',
        className,
      )}
    >
      <table className="w-full table-auto border-collapse">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              key={headerGroup.id}
              className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
            >
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={twMerge(
                    'px-4 py-1.5 text-left text-sm font-medium whitespace-nowrap text-gray-600 dark:text-gray-400',
                    header.column.getCanSort() && 'cursor-pointer select-none',
                    (header.column.columnDef.meta as any)?.className,
                  )}
                  style={{
                    width: header.getSize(),
                    minWidth: header.getSize(),
                    maxWidth: header.getSize(),
                  }}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="bg-white dark:bg-gray-900">
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td
                colSpan={table.getAllColumns().length}
                className="px-4 py-8 text-center"
              >
                <div className="flex flex-col items-center justify-center">
                  <div className="mb-2 text-gray-400 dark:text-gray-500">
                    <svg
                      className="h-8 w-8"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {emptyMessage}
                  </p>
                </div>
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row, index) => (
              <tr
                key={row.id}
                className={twMerge(
                  'transition-colors duration-150 hover:bg-gray-50/50 dark:hover:bg-gray-800/50',
                  onRowClick && 'cursor-pointer',
                  row.getIsSelected() &&
                    'bg-blue-50/30 dark:bg-blue-900/30 dark:bg-blue-950/30',
                  index < table.getRowModel().rows.length - 1 &&
                    'border-b border-[#e5e5e5] dark:border-gray-700',
                )}
                onClick={() => onRowClick?.(row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={twMerge(
                      'px-4 py-3 text-sm text-gray-900 dark:text-gray-100',
                      (cell.column.columnDef.meta as any)?.align === 'center' &&
                        'text-center',
                      (cell.column.columnDef.meta as any)?.align === 'right' &&
                        'text-right',
                    )}
                    style={{
                      width: cell.column.getSize(),
                      minWidth: cell.column.getSize(),
                      maxWidth: cell.column.getSize(),
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      ...(cell.column.columnDef.meta as any)?.style,
                    }}
                  >
                    <div
                      className={
                        columns.find((col) => col.key === cell.column.id)?.icon
                          ? ''
                          : 'break-words'
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <Card
      className={cn('h-full', className)}
      isLoading={isLoading}
      loadingContent={renderLoadingContent()}
      hoverable={false}
    >
      <div className="flex h-full flex-col">
        {(title || subtitle) && (
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                {title && (
                  <h3 className="text-xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
                    {title}
                  </h3>
                )}
                {subtitle && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {subtitle}
                  </p>
                )}
              </div>
            </div>
          </CardHeader>
        )}

        <CardContent
          className={cn(
            'flex-1 p-0',
            className?.includes('h-full') && 'flex-1',
          )}
        >
          {renderTable()}
        </CardContent>

        {/* Pagination */}
        {showPagination && (
          <CardFooter className="border-t border-[#e5e5e5] bg-white px-6 py-4 dark:bg-gray-900">
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('common:show')}
                  </span>
                  <select
                    value={table.getState().pagination.pageSize}
                    onChange={(e) => {
                      table.setPageSize(Number(e.target.value));
                    }}
                    className="rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-700"
                  >
                    {[10, 20, 30, 40, 50].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))}
                  </select>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('entries')}
                  </span>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('showingFromToOfResults', {
                    from:
                      table.getState().pagination.pageIndex *
                        table.getState().pagination.pageSize +
                      1,
                    to: Math.min(
                      (table.getState().pagination.pageIndex + 1) *
                        table.getState().pagination.pageSize,
                      table.getFilteredRowModel().rows.length,
                    ),
                    total: table.getFilteredRowModel().rows.length,
                  })}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="rounded border border-gray-200 bg-white px-2 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800"
                >
                  <ChevronLeftIcon />
                </button>
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="rounded border border-gray-200 bg-white px-2 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800"
                >
                  <ChevronRightIcon />
                </button>
              </div>
            </div>
          </CardFooter>
        )}
      </div>
    </Card>
  );
};
